import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { StarIcon, ShieldCheckIcon, AwardIcon, TagIcon, ClockIcon } from 'lucide-react';
function CountUp({
  target,
  suffix = '',
  duration = 1500
}: {
  target: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref} data-id="element-340">
      {count}
      {suffix}
    </span>;
}
const BADGES = [{
  icon: StarIcon,
  value: '4.8',
  label: '56+ Google Reviews',
  sub: 'Star Rating',
  color: '#FFD400',
  bg: 'rgba(255,212,0,0.12)',
  border: 'rgba(255,212,0,0.3)',
  fill: true
}, {
  icon: ShieldCheckIcon,
  value: '✓',
  label: 'Licensed & Insured',
  sub: 'Full Coverage',
  color: '#0B7AC4',
  bg: 'rgba(11,122,196,0.12)',
  border: 'rgba(11,122,196,0.3)',
  fill: false
}, {
  icon: AwardIcon,
  value: '10+',
  label: 'Years Experience',
  sub: 'Trusted Experts',
  color: '#7AC943',
  bg: 'rgba(122,201,67,0.12)',
  border: 'rgba(122,201,67,0.3)',
  fill: false
}, {
  icon: TagIcon,
  value: '$0',
  label: 'Free Estimates',
  sub: 'No Hidden Fees',
  color: '#F97316',
  bg: 'rgba(249,115,22,0.12)',
  border: 'rgba(249,115,22,0.3)',
  fill: false
}, {
  icon: ClockIcon,
  value: '24h',
  label: 'Fast Response',
  sub: 'We Value Your Time',
  color: '#7DD3FC',
  bg: 'rgba(125,211,252,0.12)',
  border: 'rgba(125,211,252,0.3)',
  fill: false
}];
export function TrustSection() {
  return <section className="relative py-24 bg-[#0A1628] overflow-hidden" data-id="element-341">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-342">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] rounded-full" style={{
        background: 'radial-gradient(ellipse, rgba(11,122,196,0.18) 0%, transparent 70%)'
      }} data-id="element-343" />
      </div>

      {/* Dot grid */}
      <div className="absolute inset-0 dot-grid-bg opacity-20 pointer-events-none" data-id="element-344" />

      {/* Wave top separator */}
      <div className="wave-top" data-id="element-345">
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" data-id="element-346">
          <path d="M0,30 C300,60 900,0 1200,30 L1200,0 L0,0 Z" fill="#f9fafb" data-id="element-347" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-348">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-349">
          <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-350">
            Our Credentials
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-351">
            Why Homeowners Trust Us
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] mx-auto rounded-full" data-id="element-352" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5" data-id="element-353">
          {BADGES.map((badge, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="group relative rounded-xl p-6 text-center transition-all duration-300 hover:-translate-y-1" style={{
          background: badge.bg,
          border: `1px solid ${badge.border}`
        }} data-id="element-354">
              {/* Icon circle */}
              <div className="w-14 h-14 mx-auto rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110" style={{
            background: badge.bg,
            border: `1px solid ${badge.border}`
          }} data-id="element-355">
                <badge.icon className="w-7 h-7 transition-all duration-300" style={{
              color: badge.color,
              fill: badge.fill ? badge.color : 'none'
            }} data-id="element-356" />
              </div>

              <div className="text-3xl font-bold text-white mb-1" style={{
            color: badge.color
          }} data-id="element-357">
                {badge.value}
              </div>
              <h3 className="text-white font-bold text-sm mb-1 uppercase tracking-wider" data-id="element-358">
                {badge.label}
              </h3>
              <p className="text-gray-500 text-xs" data-id="element-359">{badge.sub}</p>

              {/* Hover glow */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
            boxShadow: `inset 0 0 30px ${badge.bg}`
          }} data-id="element-360" />
            </motion.div>)}
        </div>

        {/* License badge */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="mt-12 flex justify-center" data-id="element-361">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full" style={{
          background: 'rgba(122,201,67,0.1)',
          border: '1px solid rgba(122,201,67,0.3)'
        }} data-id="element-362">
            <ShieldCheckIcon className="w-5 h-5 text-[#7AC943]" data-id="element-363" />
            <span className="text-gray-300 font-semibold tracking-wide text-sm" data-id="element-364">
              NJ Home Improvement Contractor · NJHICRA #13VH08498200
            </span>
          </div>
        </motion.div>
      </div>

      {/* Wave bottom separator */}
      <div className="wave-bottom" data-id="element-365">
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" data-id="element-366">
          <path d="M0,30 C300,60 900,0 1200,30 L1200,60 L0,60 Z" fill="#f9fafb" data-id="element-367" />
        </svg>
      </div>
    </section>;
}